import { Box, Typography } from "@mui/material";
import InfoPopper from "../popper/InfoPopper";
import {
  DateView,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CSSProperties, useMemo } from "react";
import "dayjs/locale/th";
import { CustomFormHelperText } from "./CustomFormHelperText";
import { common } from "../../i18n";
import { Calendar } from "../icon/Calendar";

export const iconStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "12px",
  transform: "translate(0, -50%)",
  pointerEvents: "none",
};

export const inputProps = {
  style: {
    padding: "0px 0px !important",
    width: "100%",
    marginLeft: "32px",
  },
};

interface Props {
  label: string;
  info: string;
  disabled?: boolean;
  required?: boolean;
  defaultDate: Dayjs | null;
  value: Dayjs | null;
  inputRef?: any;
  error?: FieldError;
  handleOnChange?: (value: Dayjs | null) => void;
  format?: string;
  views?: DateView[];
  showInfo?: boolean;
  disableFuture?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  availableYears?: number[]; // ✅ รายการปีที่อนุญาตให้เลือกเท่านั้น
  placeholder?: string;
}

const CustomDatePicker2 = (props: Props) => {
  const { t, i18n } = useTranslation([common], { nsMode: "fallback" });

  // ✅ คำนวณปีที่ต้อง disable อัตโนมัติ
  const disabledYears = useMemo(() => {
    if (!props.availableYears || props.availableYears.length === 0) return [];

    const min = props.minDate ? props.minDate.year() : Math.min(...props.availableYears);
    const max = props.maxDate ? props.maxDate.year() : Math.max(...props.availableYears);

    const fullRange: number[] = [];
    for (let y = min; y <= max; y++) fullRange.push(y);

    // ปีที่ไม่มีใน availableYears → disable
    return fullRange.filter((y) => !props.availableYears!.includes(y));
  }, [props.availableYears, props.minDate, props.maxDate]);

  return (
    <LocalizationProvider
      adapterLocale={i18n.language.split("-")[0] ?? i18n.language}
      dateAdapter={AdapterDayjs}
    >
      {props.showInfo === undefined || props.showInfo === true ? (
        <Typography
          variant="h3"
          color={"var(--dark)"}
          sx={{ marginBottom: "8px" }}
        >
          {props.label}
          {props.required ? (
            <span style={{ color: "var(--red)" }}> * </span>
          ) : (
            <span> ({t("Optional")}) </span>
          )}
          <InfoPopper text={props.info} />
        </Typography>
      ) : null}

      <Box width={"100%"} display={"block"} position={"relative"}>
        <MobileDatePicker
          value={props.value}
          defaultValue={props.defaultDate}
          onChange={props.handleOnChange}
          format={props.format ?? "YYYY"}
          minDate={props.minDate ?? dayjs("2000-01-01")}
          maxDate={props.maxDate ?? dayjs(`${dayjs().year()}-12-31`)}
          views={props.views ?? ["year"]}
          inputRef={props.inputRef}
          yearsPerRow={3}
          slotProps={{
            textField: {
              error: !!props.error,
              size: "small",
              inputProps: {
                ...inputProps,
                placeholder: props.placeholder ?? t("Year"),
              },
            },
          }}
          sx={{
            width: "100%",
            height: "40px",
          }}
          localeText={{
            toolbarTitle: props.label,
            okButtonLabel: t("Ok"),
            cancelButtonLabel: t("Cancel"),
          }}
          disableFuture={props.disableFuture}
          disabled={props.disabled}
          label={props.showInfo === false ? props.label : undefined}
          shouldDisableYear={(date) => disabledYears.includes(date.year())}
        />

        <Calendar style={iconStyle} />
      </Box>
      <CustomFormHelperText error={props.error} />
    </LocalizationProvider>
  );
};

export default CustomDatePicker2;